/**
 * File and Folder API Service
 */

import { z } from "zod/v3";
import { apiFetch } from "../utils/apiClient";
import { apiService } from "./api";
const v3BaseUrl = apiService.getBaseUrlForVersion("v3");

// Types for folder tree structure
export interface FileInfo {
	id: number;
	filename: string;
	file_path: string;
	relative_path: string | null;
	file_size: number;
	mime_type: string;
	thumbnail_path: string | null;
	created_at: string;
	updated_at: string;
	/**
	 * SHA-256 of the stored bytes (DOP-608). Lets the add-files review tell an
	 * identical re-upload from a changed file of the same name. Absent on a
	 * backend that predates it and null on rows uploaded before hashing.
	 */
	content_sha256?: string | null;
	/**
	 * Where the file is in classification. Until it is `completed` Doppler is
	 * still reading it (DOP-608). Null on rows that predate classification.
	 */
	classification_status?: "pending" | "processing" | "completed" | "failed" | null;
	classification_error?: string | null;
	/**
	 * What classification read off the file: document type, borrower, period.
	 * Absent on a backend that predates it and null until classification has
	 * written metadata. Drives the Files screens' "What it is" grouping.
	 */
	document?: FileDocumentSummary | null;
	/**
	 * Who added the file: the uploader, or the owner for files that predate
	 * Deal Room sharing. Absent on a backend that predates sharing.
	 */
	added_by?: UserRef | null;
}

/** A person, as the Deal Room screens name them. */
export interface UserRef {
	id: string;
	name: string;
}

export type FolderShareAccess = "edit" | "view";

/** Caller's effective access to a folder. */
export type FolderAccess = "owner" | FolderShareAccess;

/** Who a share is for: everyone on the team, or the people named on it. */
export type FolderShareAudience = "team" | "people";

/** One person a folder is shared with, and what they may do. */
export interface ShareMember {
	id: string;
	name: string;
	email: string;
	access: FolderShareAccess;
}

/** A teammate a folder can be shared with. */
export interface TeamMember {
	id: string;
	name: string;
	email: string;
}

/** The share covering a folder: its own, or one on an ancestor. */
export interface FolderShareInfo {
	/** Absent on a backend that predates sharing with people: the team. */
	audience?: FolderShareAudience;
	/** The team's access; for a people share, the strongest member's. */
	access: FolderShareAccess;
	shared_by?: UserRef | null;
	shared_at: string;
	/** The folder the share is set on (an ancestor when inherited). */
	folder_id: number;
	/**
	 * A people share's members. Sent to the owner and to the members
	 * themselves only; null for a team share.
	 */
	members?: ShareMember[] | null;
}

/** `PUT /files/folders/{id}/share`: the whole team, or exactly these people. */
export type ShareFolderRequest =
	| { audience: "team"; access: FolderShareAccess }
	| {
			audience: "people";
			members: { user_id: string; access: FolderShareAccess }[];
	  };

export interface FileDocumentSummary {
	document_type?: string | null;
	borrower_entity_name?: string | null;
	document_date?: string | null;
	period_start_date?: string | null;
	period_end_date?: string | null;
	page_count?: number | null;
}

export interface FolderInfo {
	id: number;
	name: string;
	path: string;
	parent_folder_id: number | null;
	is_root: boolean;
	file_count: number;
	total_size: number;
	created_at: string;
	updated_at: string;
	parent_path: string | null;
	/**
	 * The deal room this folder belongs to. Optional because a backend that
	 * predates DOP-250 omits it; the sidebar just shows no classification
	 * spinner for that row rather than guessing by name.
	 */
	project_id?: string | null;
	/**
	 * Deal Room sharing. Absent on a backend that predates it, which reads as
	 * "yours, private" everywhere.
	 */
	owner?: UserRef | null;
	shared?: FolderShareInfo | null;
	my_access?: FolderAccess | null;
}

export interface FolderTreeNode {
	folder: FolderInfo;
	sub_folders: FolderTreeNode[];
	files: FileInfo[];
}

export interface CreateFolderPayload {
	name: string;
	parent_folder_id?: number | null;
	path?: string | null;
}

export interface BoxExportStartResponse {
	job_id: string;
	status: string;
}

export interface BoxExportAuthUrlResponse {
	auth_url: string;
}

export interface ExportJob {
	id: string;
	status: string;
	source_folder_id: number;
	source_project_id: string;
	destination_provider: string;
	destination_folder_id: string | null;
	total_files: number;
	completed_files: number;
	failed_files: number;
	total_bytes: number;
	copied_bytes: number;
	attempt_count: number;
	errors: Array<Record<string, unknown>>;
	last_error: string | null;
	created_at: string;
	updated_at: string;
	completed_at: string | null;
}

export interface FolderTreeOptions {
	/**
	 * Whether each node should carry its files. Callers that only need folder
	 * identity (the workflow composer's picker needs id/name/file_count) pass
	 * `false` and skip the per-folder file listing, which is what makes the tree
	 * slow on a large portfolio.
	 */
	includeFiles?: boolean;
}

/**
 * Get the complete folder tree for the current user
 *
 * `include_files=false` is a hint, not a contract: a backend that predates the
 * param ignores it and returns the same shape with files populated, so no caller
 * is coupled to the deployment order.
 */
export const getUserFolderTree = async (
	options?: FolderTreeOptions,
): Promise<FolderTreeNode[]> => {
	const query = options?.includeFiles === false ? "?include_files=false" : "";
	return apiService.get<FolderTreeNode[]>(
		`${v3BaseUrl}/files/folders/tree${query}`,
	);
};

/**
 * Create a folder for the current user
 */
export const createFolder = async (
	payload: CreateFolderPayload,
): Promise<FolderInfo> => {
	return apiService.post<FolderInfo>("/files/folders", payload, {
		"Content-Type": "application/json",
	});
};

/**
 * List folders (optionally by parent folder ID)
 */
export const listFolders = async (
	parentFolderId?: number,
): Promise<FolderInfo[]> => {
	const endpoint = parentFolderId
		? `/files/folders?parent_folder_id=${parentFolderId}`
		: "/files/folders";
	return apiService.get<FolderInfo[]>(endpoint);
};

/**
 * Get a specific folder with its contents
 */
export const getFolder = async (
	folderId: number,
	includeFiles: boolean = true,
	includeSubFolders: boolean = true,
	recursive: boolean = false,
): Promise<FolderTreeNode> => {
	const params = new URLSearchParams({
		include_files: String(includeFiles),
		include_sub_folders: String(includeSubFolders),
		recursive: String(recursive),
	});
	return apiService.get<FolderTreeNode>(`/files/folders/${folderId}?${params}`);
};

/**
 * Delete a folder (cascades to subfolders and files)
 */
export const deleteFolder = async (folderId: number): Promise<void> => {
	const response = await apiFetch(
		`${v3BaseUrl}/files/folders/${folderId}`,
		{ method: "DELETE" },
	);
	if (!response.ok) {
		throw new Error(`Failed to delete folder: ${response.status}`);
	}
};

/**
 * Delete one file from a deal room (DOP-608). The backend re-derives the deal's
 * catalog afterwards, so the file stops being used in answers.
 */
export const deleteFile = async (fileId: number): Promise<void> => {
	const response = await apiFetch(`${v3BaseUrl}/files/${fileId}`, {
		method: "DELETE",
	});
	if (!response.ok) {
		throw new Error(`Failed to delete file: ${response.status}`);
	}
};

export interface SignedUrlResponse {
	signed_url: string;
	expires_in: number;
	expires_at: string;
	signing_method?: string;
}

export const getFileSignedUrl = async (
	fileId: number,
	expiresIn: number = 300,
	page?: number,
): Promise<SignedUrlResponse> => {
	const params = new URLSearchParams({
		expires_in: String(expiresIn),
	});
	if (page !== undefined && page > 0) {
		params.append("page", String(page));
	}
	return apiService.get<SignedUrlResponse>(
		`${v3BaseUrl}/files/${fileId}/signed-url?${params}`,
	);
};

/**
 * Download a source file's bytes through the backend proxy.
 *
 * Used by the in-app viewers (e.g. the Excel sheet renderer) instead of
 * fetching a GCS signed URL directly: the request stays same-origin to the
 * backend (already CORS-allowed for the UI origin), so per-tenant buckets
 * don't each need browser-origin CORS configured.
 */
export const downloadFileBytes = async (
	fileId: number,
): Promise<ArrayBuffer> => {
	return (await downloadFile(fileId)).bytes;
};

/** Parse a filename out of a ``Content-Disposition`` header, if present. */
const filenameFromContentDisposition = (header: string | null): string | null => {
	if (!header) return null;
	// Prefer the RFC 5987 ``filename*=UTF-8''...`` form, then plain ``filename=``.
	const star = header.match(/filename\*\s*=\s*(?:UTF-8'')?"?([^";]+)"?/i);
	if (star?.[1]) {
		try {
			return decodeURIComponent(star[1]).trim() || null;
		} catch {
			return star[1].trim() || null;
		}
	}
	const plain = header.match(/filename\s*=\s*"?([^";]+)"?/i);
	return plain?.[1]?.trim() || null;
};

/**
 * Download a source file's bytes plus its server-provided filename.
 *
 * The backend stamps the original filename into ``Content-Disposition``; the
 * in-app viewer uses it to title itself even when the citation that opened it
 * carried only a ``file_id`` (e.g. older extraction artifacts).
 */
export const downloadFile = async (
	fileId: number,
): Promise<{ bytes: ArrayBuffer; filename: string | null }> => {
	const res = await apiFetch(`${v3BaseUrl}/files/${fileId}/download`);
	if (!res.ok) {
		throw new Error(`File download failed (${res.status}).`);
	}
	const filename = filenameFromContentDisposition(
		res.headers.get("Content-Disposition"),
	);
	return { bytes: await res.arrayBuffer(), filename };
};

/**
 * Ask the backend for a short-lived download link and hand it to the browser.
 *
 * A download is a navigation, not a fetch: the browser then streams the bytes
 * straight to disk (a whole deal can be gigabytes) and names the file from the
 * server's ``Content-Disposition`` — the uploaded filename, never an id. A
 * navigation can't carry the auth header, so the link carries a two-minute
 * token minted by the authenticated POST.
 */
const saveViaDownloadLink = async (linkPath: string): Promise<void> => {
	const res = await apiFetch(`${v3BaseUrl}${linkPath}`, { method: "POST" });
	if (!res.ok) {
		throw new Error(`Couldn't start the download (${res.status}).`);
	}
	const { url } = (await res.json()) as { url: string };
	const apiOrigin = new URL(v3BaseUrl, window.location.origin).origin;
	const a = document.createElement("a");
	a.href = new URL(url, apiOrigin).toString();
	a.rel = "noopener";
	document.body.appendChild(a);
	a.click();
	a.remove();
};

/** Save one Deal Room file under the name it was uploaded with. */
export const saveFileToDisk = (fileId: number): Promise<void> =>
	saveViaDownloadLink(`/files/${fileId}/download-link`);

/** Save a whole deal (or one of its folders) as a zip, folders kept. */
export const saveFolderToDisk = (folderId: number): Promise<void> =>
	saveViaDownloadLink(`/files/folders/${folderId}/download-link`);

/**
 * Box export is owner-only (the backend refuses anyone else). Callers gate the
 * action with `canExportFolder` from utils/folderSharing.
 */
export const startBoxExport = async (
	folderId: number,
): Promise<BoxExportStartResponse> => {
	const response = await apiFetch(`${v3BaseUrl}/files/folders/${folderId}/exports/box`, {
		method: "POST",
	});
	if (!response.ok) {
		const detail = await response.json().catch(() => ({}));
		const error = new Error(`Box export failed: ${response.status}`);
		(error as Error & { status?: number; detail?: unknown }).status =
			response.status;
		(error as Error & { status?: number; detail?: unknown }).detail = detail;
		throw error;
	}
	return response.json();
};

export const getBoxExportAuthUrl = async (
	folderId: number,
): Promise<BoxExportAuthUrlResponse> => {
	return apiService.get<BoxExportAuthUrlResponse>(
		`${v3BaseUrl}/files/folders/${folderId}/exports/box/auth-url`,
	);
};

export const completeBoxExportOAuth = async (
	code: string,
	state: string,
): Promise<BoxExportStartResponse> => {
	const response = await apiFetch(`${v3BaseUrl}/exports/box/oauth-callback`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ code, state }),
	});
	if (!response.ok) {
		throw new Error(`Box export OAuth failed: ${response.status}`);
	}
	return response.json();
};

export const getExportJob = async (jobId: string): Promise<ExportJob> => {
	return apiService.get<ExportJob>(`${v3BaseUrl}/exports/${jobId}`);
};

const folderShareSchema = z.object({
	audience: z.enum(["team", "people"]).optional(),
	access: z.enum(["edit", "view"]),
	shared_by: z.object({ id: z.string(), name: z.string() }).nullish(),
	shared_at: z.string(),
	folder_id: z.number(),
	members: z
		.array(
			z.object({
				id: z.string(),
				name: z.string(),
				email: z.string(),
				access: z.enum(["edit", "view"]),
			}),
		)
		.nullish(),
});

const teamMembersSchema = z.array(
	z.object({ id: z.string(), name: z.string(), email: z.string() }),
);

const shareError = async (response: Response, action: string): Promise<Error> => {
	const detail = await response.json().catch(() => ({}));
	// The server's `detail` is written for a person ("Only the owner can share
	// this folder"); fall back to a status line, which callers swap out.
	const message =
		typeof detail?.detail === "string"
			? detail.detail
			: `${action} failed (${response.status}).`;
	const error = new Error(message);
	(error as Error & { status?: number; detail?: unknown }).status = response.status;
	(error as Error & { status?: number; detail?: unknown }).detail = detail;
	return error;
};

/**
 * Share a Deal Room folder with everyone on the team or with specific people
 * on it, or change how. A people request replaces the member list. Owner only.
 */
export const shareFolder = async (
	folderId: number,
	request: ShareFolderRequest,
): Promise<FolderShareInfo> => {
	const response = await apiFetch(`${v3BaseUrl}/files/folders/${folderId}/share`, {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(request),
	});
	if (!response.ok) throw await shareError(response, "Sharing");
	return folderShareSchema.parse(await response.json());
};

/**
 * The people the owner can share a folder with: the folder's active
 * teammates. Owner only (404 for anyone else).
 */
export const getShareCandidates = async (
	folderId: number,
): Promise<TeamMember[]> => {
	const response = await apiFetch(
		`${v3BaseUrl}/files/folders/${folderId}/share/candidates`,
	);
	if (!response.ok) throw await shareError(response, "Loading your team");
	return teamMembersSchema.parse(await response.json());
};

/** Stop sharing a Deal Room folder. Owner only. */
export const unshareFolder = async (folderId: number): Promise<void> => {
	const response = await apiFetch(`${v3BaseUrl}/files/folders/${folderId}/share`, {
		method: "DELETE",
	});
	if (!response.ok) throw await shareError(response, "Stop sharing");
};
